import React from "react";
import { X, ArrowRight, Info, Layers, Sparkles } from "lucide-react";
import { cn } from "../../lib/utils";
import type { MapSidePanelSurfaceProps } from "../../contracts/map-workspace";

export function MapSidePanelSurface({
  title,
  subtitle,
  description,
  badges = [],
  summary = [],
  headerMode = "default",
  hideDescription = false,
  hideSummary = false,
  content,
  actions = [],
  onAction,
  onInspect,
  onClear,
  className,
}: MapSidePanelSurfaceProps) {
  const hasCompactBadges = badges.length > 0;
  const hasCompactHeader = headerMode === "compact" && (hasCompactBadges || Boolean(onClear));

  return (
    <div className={cn("flex flex-col h-full bg-background border-l border-border", className)}>
      {headerMode === "compact" ? (
        hasCompactHeader ? (
          <header
            className={cn(
              "shrink-0",
              hasCompactBadges
                ? "border-b border-border bg-muted/5 px-4 py-2.5"
                : "bg-transparent px-2 pt-2 pb-0",
            )}
          >
            <div
              className={cn(
                "flex items-center",
                hasCompactBadges ? "justify-between gap-3" : "justify-end",
              )}
            >
              {hasCompactBadges ? (
                <div className="flex min-w-0 flex-wrap gap-1.5">
                  {badges.map((badge, i) => (
                    <span
                      key={i}
                      className="px-2 py-1 text-[10px] font-black uppercase tracking-[0.1em] rounded-sm bg-primary text-primary-foreground shadow-sm"
                    >
                      {badge}
                    </span>
                  ))}
                </div>
              ) : null}
              {onClear ? (
                <button
                  onClick={onClear}
                  aria-label="Close panel"
                  className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              ) : null}
            </div>
          </header>
        ) : null
      ) : (
        <header className="px-5 py-4 border-b border-border bg-muted/5 shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex flex-col min-w-0">
              <h2 className="text-base font-black text-foreground tracking-tight leading-none uppercase">
                {title}
              </h2>
              {subtitle && (
                <p className="text-xs font-bold text-primary mt-2 uppercase tracking-widest opacity-80">
                  {subtitle}
                </p>
              )}
            </div>
            {onClear && (
              <button
                onClick={onClear}
                aria-label="Close panel"
                className="p-2 -mt-2 -mr-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {badges.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {badges.map((badge, i) => (
                <span
                  key={i}
                  className="px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.1em] rounded-sm bg-primary text-primary-foreground shadow-sm"
                >
                  {badge}
                </span>
              ))}
            </div>
          )}
        </header>
      )}

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className={cn(headerMode === "compact" ? "space-y-4 p-4 pt-3" : "p-5 space-y-5")}>
          {/* Description */}
          {!hideDescription && description && (
            <div className="space-y-2">
               <div className="flex items-center gap-2 text-muted-foreground">
                 <Info className="w-3.5 h-3.5" />
                 <span className="text-[10px] font-black uppercase tracking-widest">Description</span>
               </div>
               <p className="text-sm leading-relaxed text-foreground/80">
                 {description}
               </p>
            </div>
          )}

          {/* Summary Stats Grid — only rendered when there are items */}
          {!hideSummary && summary.length > 0 && (
            <div className="space-y-3">
               <div className="flex items-center gap-2 text-muted-foreground">
                 <Sparkles className="w-3.5 h-3.5" />
                 <span className="text-[10px] font-black uppercase tracking-widest">Key Metrics</span>
               </div>
               <div className="grid grid-cols-1 gap-px bg-border border border-border rounded overflow-hidden">
                 {summary.map((item) => (
                   <div key={item.key} className="bg-background p-3 flex items-center justify-between">
                     <span className="text-xs font-bold text-muted-foreground uppercase tracking-tight">{item.label}</span>
                     <span className="text-sm font-black text-foreground font-mono">{item.value}</span>
                   </div>
                 ))}
               </div>
            </div>
          )}

          {content ? (
            <div className="space-y-4">
              {content}
            </div>
          ) : null}

          {/* Secondary Actions / Inspection */}
          {onInspect && (
            <button
              onClick={onInspect}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-md border border-border bg-muted/20 hover:bg-muted/40 transition-all group"
            >
              <div className="flex items-center gap-2.5">
                <Layers className="w-3.5 h-3.5 text-primary shrink-0" />
                <span className="text-xs font-medium text-foreground">Technical Inspection</span>
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
            </button>
          )}
        </div>
      </div>

      {/* Actions Footer */}
      {actions.length > 0 && (
        <footer className="p-4 border-t border-border bg-background shrink-0 space-y-2">
          {actions.map((action) => (
            <button
              key={action.id}
              disabled={action.disabled}
              onClick={() => onAction?.(action.id)}
              className={cn(
                "w-full flex items-center justify-center gap-2 h-12 rounded-md text-sm font-black transition-all group shadow-sm",
                "bg-primary text-primary-foreground hover:brightness-110 active:scale-[0.99]",
                "disabled:opacity-50 disabled:grayscale disabled:cursor-not-allowed disabled:active:scale-100"
              )}
            >
              <span>{action.label}</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          ))}
        </footer>
      )}
    </div>
  );
}
